import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BarChart3, Loader2, ExternalLink, Download, Wand2, Pencil, Check, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import UtmBulkTagger from "./UtmBulkTagger";

const RANGES = [
    { key: "7d", label: "7d" },
    { key: "30d", label: "30d" },
    { key: "90d", label: "90d" },
    { key: "all", label: "All time" },
];

/**
 * Admin → Attribution.
 *
 * Answers "which channel drove that revenue?" by joining the first-touch
 * UTM stamp on users with the payment_ledger. Zero data-entry — the numbers
 * come from what the SPA already captured at signup.
 */
export default function AdminAttribution() {
    const [range, setRange] = useState("30d");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [backfilling, setBackfilling] = useState(false);

    const fetchData = async (r = range) => {
        try {
            setLoading(true);
            const { data } = await api.get(`/admin/attribution/channels?range=${r}`);
            setData(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { fetchData(range); }, [range]);  // eslint-disable-line react-hooks/exhaustive-deps

    const runBackfill = async () => {
        try {
            setBackfilling(true);
            const { data } = await api.post("/admin/attribution/backfill");
            const total = data?.total || 0;
            if (total === 0) toast.info("Nothing to backfill — every user already has an attribution stamp.");
            else toast.success(`Stamped ${total} legacy user${total === 1 ? "" : "s"}.`);
            await fetchData(range);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBackfilling(false);
        }
    };

    const downloadCsv = () => {
        // Server-side download so the browser prompts a save-as with correct
        // Content-Disposition. Include the admin bearer via a query param
        // fallback would be insecure — instead we build a fetch → blob so the
        // axios interceptor attaches Authorization.
        api.get(`/admin/attribution/channels.csv?range=${range}`, { responseType: "blob" })
            .then((res) => {
                const blob = new Blob([res.data], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `simonara-attribution-${range}.csv`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
            })
            .catch((err) => toast.error(formatApiError(err)));
    };

    const totalRevenue = (data?.channels || []).reduce((s, c) => s + (c.revenue || 0), 0) + (data?.unattributed?.revenue || 0);
    const totalPaying = (data?.channels || []).reduce((s, c) => s + (c.paying || 0), 0) + (data?.unattributed?.paying || 0);
    const totalSignups = (data?.channels || []).reduce((s, c) => s + (c.signups || 0), 0) + (data?.unattributed?.signups || 0);
    const totalSpend = (data?.channels || []).reduce((s, c) => s + (c.spend_in_range || 0), 0);
    const blendedRoas = totalSpend > 0 ? (totalRevenue / totalSpend) : null;

    // Inline monthly-spend editor. Persisted to channel_costs so CAC / ROAS
    // survive a reload. Only shown for known channels (empty utm_source is
    // the Unattributed row).
    const [editingSrc, setEditingSrc] = useState(null);
    const [editingVal, setEditingVal] = useState("");
    const [savingSrc, setSavingSrc] = useState(null);
    const startEdit = (c) => {
        setEditingSrc(c.utm_source);
        setEditingVal(String(c.monthly_spend || ""));
    };
    const cancelEdit = () => { setEditingSrc(null); setEditingVal(""); };
    const saveEdit = async (src) => {
        const parsed = Number(editingVal);
        if (Number.isNaN(parsed) || parsed < 0) {
            toast.error("Enter a non-negative dollar amount");
            return;
        }
        try {
            setSavingSrc(src);
            await api.put(`/admin/attribution/channels/${encodeURIComponent(src)}/spend`, { monthly_spend: parsed });
            toast.success(`Spend for ${src} set to $${parsed.toFixed(2)}/mo`);
            await fetchData(range);
            cancelEdit();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSavingSrc(null);
        }
    };
    const fmtCurrency = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
    const fmtRoas = (v) => (v == null ? "—" : `${Number(v).toFixed(2)}×`);
    const roasClass = (v) => (v == null ? "text-muted-foreground" : v >= 3 ? "text-emerald-600" : v >= 1 ? "text-amber-600" : "text-rose-600");

    return (
        <div data-testid="admin-attribution" className="max-w-6xl mx-auto py-8 px-6">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-violet-600 font-semibold">
                        <BarChart3 size={13} /> Marketing attribution
                    </div>
                    <h1 className="mt-1 font-heading text-2xl font-semibold text-foreground">Where paying customers came from</h1>
                    <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
                        First-touch attribution. If a signup arrived via <code>?utm_source=linkedin</code> and later paid, their revenue lands here under &quot;linkedin&quot;.
                    </p>
                </div>
                <div className="inline-flex bg-muted/40 rounded-lg p-1 border border-border">
                    {RANGES.map((r) => (
                        <button
                            key={r.key}
                            onClick={() => setRange(r.key)}
                            data-testid={`attr-range-${r.key}`}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${range === r.key ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={runBackfill}
                        disabled={backfilling}
                        data-testid="attr-backfill-btn"
                        className="text-xs font-medium px-3 py-2 rounded-md border border-border hover:bg-muted/50 transition inline-flex items-center gap-1.5 disabled:opacity-60"
                        title="Stamp historical users from referral / auth-provider / paid-customer signals"
                    >
                        {backfilling ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                        Backfill
                    </button>
                    <button
                        onClick={downloadCsv}
                        data-testid="attr-csv-btn"
                        className="text-xs font-medium px-3 py-2 rounded-md border border-border hover:bg-muted/50 transition inline-flex items-center gap-1.5"
                    >
                        <Download size={12} /> CSV
                    </button>
                </div>
            </div>

            {loading && (
                <div className="py-16 flex justify-center"><Loader2 size={20} className="animate-spin text-violet-500" /></div>
            )}
            {!loading && data && (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-6">
                        <TotalTile label="Total signups" value={totalSignups.toLocaleString()} />
                        <TotalTile label="Paying customers" value={totalPaying.toLocaleString()} />
                        <TotalTile label="Attributed revenue" value={`$${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} accent />
                        <TotalTile
                            label="Blended ROAS"
                            value={blendedRoas == null ? "—" : `${blendedRoas.toFixed(2)}×`}
                            hint={totalSpend > 0
                                ? `on $${totalSpend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} spend`
                                : "add channel spend to unlock"}
                            testId="attr-blended-roas-tile"
                        />
                    </div>
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="card-soft overflow-hidden">
                        <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[880px]">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground bg-muted/40">
                                    <th className="px-4 py-2 font-medium">Channel</th>
                                    <th className="px-4 py-2 font-medium">Signups</th>
                                    <th className="px-4 py-2 font-medium">Paying</th>
                                    <th className="px-4 py-2 font-medium">Revenue</th>
                                    <th className="px-4 py-2 font-medium" title="Monthly ad spend — prorated to the current range for CAC / ROAS.">Spend</th>
                                    <th className="px-4 py-2 font-medium" title="Revenue per signup (RPV). Revenue ÷ signups.">Rev / Signup</th>
                                    <th className="px-4 py-2 font-medium" title="Cost per signup (CPS). Spend ÷ signups.">Cost / Signup</th>
                                    <th className="px-4 py-2 font-medium" title="Cost per paying customer (CAC). Spend ÷ paying.">CAC</th>
                                    <th className="px-4 py-2 font-medium" title="Return on ad spend. Revenue ÷ spend.">ROAS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.channels.length === 0 && (
                                    <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">No attributed signups yet. Add <code>?utm_source=...</code> to your outbound links to start tracking.</td></tr>
                                )}
                                {data.channels.map((c) => (
                                    <tr key={c.utm_source} data-testid={`attr-row-${c.utm_source}`} className="border-t border-border align-top">
                                        <td className="px-4 py-3 font-medium text-foreground">
                                            {c.display_name || c.utm_source}
                                            <div className="mt-1 text-[10px] text-muted-foreground font-normal">
                                                {c.signups > 0 ? `Conv. ${((c.paying / c.signups) * 100).toFixed(1)}%` : ""}
                                                {c.sample_campaigns?.length ? <span className="ml-2">• {c.sample_campaigns.slice(0, 2).join(", ")}</span> : null}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 mono-score">{c.signups}</td>
                                        <td className="px-4 py-3 mono-score">{c.paying}</td>
                                        <td className="px-4 py-3 mono-score font-semibold text-emerald-600">
                                            ${c.revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            {c.revenue_by_plan && Object.keys(c.revenue_by_plan).length > 0 && (
                                                <div className="mt-1 flex flex-wrap gap-1" data-testid={`attr-plan-breakdown-${c.utm_source}`}>
                                                    {Object.entries(c.revenue_by_plan).sort((a, b) => b[1] - a[1]).map(([plan, amt]) => (
                                                        <span key={plan} className="text-[10px] px-1.5 py-0.5 rounded bg-muted/50 text-muted-foreground font-normal">
                                                            {plan.replace(/_/g, " ")}: ${Number(amt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </td>
                                        {/* Editable monthly spend */}
                                        <td className="px-4 py-3 mono-score" data-testid={`attr-spend-cell-${c.utm_source}`}>
                                            {editingSrc === c.utm_source ? (
                                                <div className="inline-flex items-center gap-1">
                                                    <span className="text-muted-foreground">$</span>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={editingVal}
                                                        onChange={(e) => setEditingVal(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") saveEdit(c.utm_source);
                                                            if (e.key === "Escape") cancelEdit();
                                                        }}
                                                        autoFocus
                                                        data-testid={`attr-spend-input-${c.utm_source}`}
                                                        className="w-24 px-2 py-1 rounded border border-border bg-background text-sm"
                                                        placeholder="0.00"
                                                    />
                                                    <button
                                                        onClick={() => saveEdit(c.utm_source)}
                                                        disabled={savingSrc === c.utm_source}
                                                        data-testid={`attr-spend-save-${c.utm_source}`}
                                                        className="p-1 rounded hover:bg-emerald-500/10 text-emerald-600 disabled:opacity-60"
                                                        title="Save"
                                                    >
                                                        {savingSrc === c.utm_source
                                                            ? <Loader2 size={12} className="animate-spin" />
                                                            : <Check size={12} />}
                                                    </button>
                                                    <button
                                                        onClick={cancelEdit}
                                                        data-testid={`attr-spend-cancel-${c.utm_source}`}
                                                        className="p-1 rounded hover:bg-rose-500/10 text-rose-500"
                                                        title="Cancel"
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => startEdit(c)}
                                                    data-testid={`attr-spend-edit-${c.utm_source}`}
                                                    className="inline-flex items-center gap-1.5 group text-left"
                                                    title={c.monthly_spend
                                                        ? `Monthly: $${Number(c.monthly_spend).toFixed(2)} — click to edit`
                                                        : "Click to set monthly ad spend for this channel"}
                                                >
                                                    <span className={c.spend_in_range ? "font-medium" : "text-muted-foreground italic"}>
                                                        {c.spend_in_range != null && c.spend_in_range > 0
                                                            ? fmtCurrency(c.spend_in_range)
                                                            : "Set spend"}
                                                    </span>
                                                    <Pencil size={10} className="opacity-40 group-hover:opacity-100 transition" />
                                                </button>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 mono-score" data-testid={`attr-rps-${c.utm_source}`}>{fmtCurrency(c.revenue_per_signup)}</td>
                                        <td className="px-4 py-3 mono-score" data-testid={`attr-cps-${c.utm_source}`}>{fmtCurrency(c.cost_per_signup)}</td>
                                        <td className="px-4 py-3 mono-score" data-testid={`attr-cac-${c.utm_source}`}>{fmtCurrency(c.cost_per_paying)}</td>
                                        <td className={`px-4 py-3 mono-score font-semibold ${roasClass(c.roas)}`} data-testid={`attr-roas-${c.utm_source}`}>{fmtRoas(c.roas)}</td>
                                    </tr>
                                ))}
                                {data.unattributed && (
                                    <tr className="border-t border-border bg-muted/20">
                                        <td className="px-4 py-3 text-muted-foreground italic">Unattributed (direct / organic)</td>
                                        <td className="px-4 py-3 mono-score">{data.unattributed.signups}</td>
                                        <td className="px-4 py-3 mono-score">{data.unattributed.paying}</td>
                                        <td className="px-4 py-3 mono-score font-semibold text-muted-foreground">${data.unattributed.revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                        <td className="px-4 py-3 text-muted-foreground">—</td>
                                        <td className="px-4 py-3 text-muted-foreground">—</td>
                                        <td className="px-4 py-3 text-muted-foreground">—</td>
                                        <td className="px-4 py-3 text-muted-foreground">—</td>
                                        <td className="px-4 py-3 text-muted-foreground">—</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        </div>
                    </motion.div>
                    <p className="mt-4 text-xs text-muted-foreground max-w-2xl">
                        <ExternalLink size={11} className="inline mr-1" />
                        Set monthly ad spend per channel to unlock CAC and ROAS. Values are prorated to the selected range (30d = 100%, 7d = ~23%, 90d = 300%). All-time hides ROI metrics.
                    </p>
                    <UtmBulkTagger />
                </>
            )}
        </div>
    );
}

const TotalTile = ({ label, value, accent = false, hint = "", testId }) => (
    <div className={`card-soft p-4 ${accent ? "border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-transparent" : ""}`} data-testid={testId}>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</div>
        <div className={`mt-1 font-heading text-2xl font-semibold ${accent ? "text-emerald-600" : "text-foreground"}`}>{value}</div>
        {hint ? <div className="mt-1 text-[10px] text-muted-foreground">{hint}</div> : null}
    </div>
);
